import { UnitInput } from "@/components/primitives/unit-input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { calculateChocolateComposition } from "@/lib/ingredients/form-estimators";
import { toNumber } from "@/lib/utils";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { CreateIngredientFormData } from "./schema";
import { SimpleFormWrapper } from "./simple-wrapper";
export { calculateChocolateComposition } from "@/lib/ingredients/form-estimators";

type SimpleFormProps = {
  form: UseFormReturn<CreateIngredientFormData>;
};

type ChocolateComposition = Pick<
  CreateIngredientFormData,
  | "fatPct"
  | "saturatedFatPct"
  | "waterPct"
  | "proteinPct"
  | "carbPct"
  | "fiberPct"
  | "alcoholPct"
  | "sugarsPct"
  | "cocoaSolidsNonfatPct"
  | "cocoaFatPct"
  | "energyKcal"
  | "pod"
  | "pac"
>;

const CHOCOLATE_DERIVED_FIELDS = [
  "fatPct",
  "saturatedFatPct",
  "waterPct",
  "proteinPct",
  "carbPct",
  "fiberPct",
  "alcoholPct",
  "cocoaSolidsNonfatPct",
  "energyKcal",
  "pod",
  "pac",
] as const satisfies ReadonlyArray<keyof ChocolateComposition>;

export function SimpleChocolateForm({ form }: SimpleFormProps) {
  const cocoaPctValue = form.watch("cocoaPct");
  const cocoaFatPctValue = form.watch("cocoaFatPct");
  const sugarsPctValue = form.watch("sugarsPct");

  useEffect(() => {
    if (String(cocoaPctValue).trim() === "") {
      return;
    }
    const calculated = calculateChocolateComposition(
      toNumber(cocoaPctValue),
      toNumber(cocoaFatPctValue),
      toNumber(sugarsPctValue),
    );
    for (const field of CHOCOLATE_DERIVED_FIELDS) {
      form.setValue(field, calculated[field]);
    }
  }, [cocoaPctValue, cocoaFatPctValue, sugarsPctValue, form]);

  return (
    <SimpleFormWrapper form={form} selectedCategory="chocolate">
      <FormField
        control={form.control}
        name="cocoaPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Cacao %</FormLabel>
            <FormDescription>
              Enter 100% for pure cocoa powder. Dark chocolate typically has{" "}
              {">"} 60% cacao, semi-sweet 40-60%, and milk chocolate {"<"}40%.
            </FormDescription>
            <FormControl>
              <UnitInput {...field} unit="%" min={0} max={100} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="cocoaFatPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Cacao Butter %</FormLabel>
            <FormDescription>
              For most chocolate products, this should match the total fat
              content.
            </FormDescription>
            <FormControl>
              <UnitInput {...field} unit="%" min={0} max={100} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="sugarsPct"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Sugar %</FormLabel>
            <FormDescription>
              This should match the total sugar content.
            </FormDescription>
            <FormControl>
              <UnitInput {...field} unit="%" min={0} max={100} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </SimpleFormWrapper>
  );
}
